import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import React, { useEffect, useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import { CircleMarker, MapContainer, Polyline, Popup, TileLayer, useMap } from 'react-leaflet';
import type { ComensalRutaMapProps } from './ComensalRutaMap.types';

export type { ComensalRutaMapProps };

function FitBounds({ userLatLng, pins, fallbackCenter, routeCoordinates }: ComensalRutaMapProps) {
  const map = useMap();
  useEffect(() => {
    const points: L.LatLngExpression[] = pins.map((p) => [p.lat, p.lng]);
    if (userLatLng) points.push([userLatLng.lat, userLatLng.lng]);
    if (routeCoordinates?.length) {
      for (const c of routeCoordinates) {
        points.push([c.lat, c.lng]);
      }
    }
    if (points.length === 0) {
      map.setView([fallbackCenter.lat, fallbackCenter.lng], 12);
      return;
    }
    const b = L.latLngBounds(points);
    map.fitBounds(b, { padding: [48, 48], maxZoom: 15 });
  }, [
    map,
    userLatLng?.lat,
    userLatLng?.lng,
    pins,
    fallbackCenter.lat,
    fallbackCenter.lng,
    routeCoordinates,
  ]);
  return null;
}

export function ComensalRutaMap(props: ComensalRutaMapProps) {
  const { userLatLng, pins, fallbackCenter } = props;
  const center = useMemo((): [number, number] => {
    const c = userLatLng ?? pins[0] ?? fallbackCenter;
    return [c.lat, c.lng];
  }, [userLatLng, pins, fallbackCenter]);

  return (
    <View style={styles.wrap}>
      <MapContainer
        center={center}
        zoom={13}
        style={{ height: '100%', width: '100%', borderRadius: 16 }}
        scrollWheelZoom
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <FitBounds {...props} />
        {props.routeCoordinates && props.routeCoordinates.length >= 2 ? (
          <Polyline
            key={props.routeCoordinates.length}
            positions={props.routeCoordinates.map((c) => [c.lat, c.lng] as [number, number])}
            pathOptions={{
              color: '#dc2626',
              weight: 7,
              opacity: 1,
              lineCap: 'round',
              lineJoin: 'round',
            }}
          />
        ) : null}
        {userLatLng ? (
          <CircleMarker
            center={[userLatLng.lat, userLatLng.lng]}
            radius={11}
            pathOptions={{ color: '#1d4ed8', fillColor: '#60a5fa', fillOpacity: 0.95, weight: 2 }}
          >
            <Popup>Tu ubicación</Popup>
          </CircleMarker>
        ) : null}
        {pins.map((p) => (
          <CircleMarker
            key={p.id}
            center={[p.lat, p.lng]}
            radius={9}
            pathOptions={{ color: '#030213', fillColor: '#1e293b', fillOpacity: 0.95, weight: 2 }}
          >
            <Popup>{p.label}</Popup>
          </CircleMarker>
        ))}
      </MapContainer>
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    flex: 1,
    minHeight: 360,
    borderRadius: 16,
    overflow: 'hidden',
    position: 'relative',
  },
});
